import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreatePageMutation,
  useGetPageQuery,
  useUpdatePageMutation
} from '../../../../../services/endpoints/website/page';
import { getCurrentUser } from '../../../../../services/helper';
import { ImageDetails, Page } from '../../../../../types/website.types';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { canEditPages } from '../../../../../utils/rbac';
import { getErrorMessage } from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { PageForm } from './PageForm';

export interface FormValues extends ImageDetails {
  id?: number;
  name: string;
  published_on: string;
  author: string;
  is_featured: boolean;
  content: string;
  button_text: string;
  button_target: string;
  button_opens_in_new_window: boolean;
  gallery_id: { label: string; value: number };
}

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

interface AddEditPageProps {
  editing?: boolean;
}

export const PageLinkSchema = Yup.object({
  text: Yup.string().optional(),
  target: Yup.string().optional(),
  link_type: Yup.string().optional()
});

export const AddEditPage: React.FC<AddEditPageProps> = ({ editing }) => {
  const params = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createPage] = useCreatePageMutation();
  const [updatePage] = useUpdatePageMutation();
  const { data, error, isLoading } = useGetPageQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const currentUser = getCurrentUser();

  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    author: '',
    published_on: '',
    is_featured: false,
    content: '',
    button_text: '',
    button_target: '',
    button_opens_in_new_window: false,
    gallery_id: { label: '', value: 0 }
  });

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    author: Yup.string().required('Author is required'),
    image: Yup.string().when('image_file_name', {
      is: (val: string) => !val,
      then: (schema) => schema.required('Image is required'),
      otherwise: (schema) => schema.optional()
    }),
    published_on: Yup.string().required('Published On is required'),
    page_links: Yup.array().of(PageLinkSchema).optional(),
    content: Yup.string().required('Content is required'),
    button_text: Yup.string().required('Button Text is required'),
    button_target: Yup.string().required('Button Target is required'),
    button_opens_in_new_window: Yup.boolean().required('Button Opens is required').default(false),
    gallery_id: Yup.object({
      value: Yup.number().min(1, 'Gallery is required').required('Gallery is required'),
      label: Yup.string().optional()
    }).required('Gallery is required')
  });

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading Page', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (!canEditPages(currentUser)) {
      addToast('You have no permissions to edit this data', 'error');
      navigate(`/admin/website/pages`);
      return;
    }

    if (data && data.page) {
      const { page } = data;
      setInitialValues({
        id: page.id,
        name: page.name,
        author: page.author,
        published_on: page.published_on,
        image_file_name: page.image_file_name,
        s3_base_url: page.s3_base_url,
        is_featured: page.is_featured,
        content: page.content,
        button_text: page.button_text,
        button_target: page.button_target,
        button_opens_in_new_window: page.button_opens_in_new_window,
        gallery_id: { label: page.gallery?.name ?? '', value: page.gallery_id ?? 0 }
      });
    }
  }, [data]);

  const handleSubmit = async (formValue: FormValues) => {
    try {
      const pageData: Page = {
        ...formValue,
        gallery_id: formValue.gallery_id.value,
        published_on: dateToUTC(new Date(formValue.published_on), 'MM/dd/yyyy')
      };
      delete pageData.image_file_name;
      delete pageData.s3_base_url;
      if (editing) {
        const res = await updatePage(pageData).unwrap();
        if (res && res.page) {
          addToast(`Page updated successfully`, 'success');
          navigate(`/admin/website/pages/${data?.page.id}`);
        }
      } else {
        const res = await createPage(pageData).unwrap();
        if (res && res.page) {
          navigate(`/admin/website/pages/${res.page.id}`);
          addToast('Page created successfully', 'success');
        } else throw new Error('A problem happened while creating Page');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to Page${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={editing ? `/admin/website/pages/${data?.page.id}` : '/admin/website/pages'}
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Page
          </h2>
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              dirty,
              touched,
              errors,
              values,
              setFieldValue,
              validateForm,
              setTouched
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  <PageForm
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={async () => {
                        if (editing && !dirty) {
                          addToast('No changes to save', 'info');
                          navigate(`/admin/website/pages/${data?.page.id}`);
                          return;
                        }
                        const validationErrors = await validateForm();
                        if (Object.keys(validationErrors).length > 0) {
                          const allTouched = Object.keys(validationErrors).reduce(
                            (acc, key) => ({ ...acc, [key]: true }),
                            {}
                          );
                          setTouched(allTouched as FormikTouched<FormValues>);
                          return;
                        }
                        submitForm();
                      }}
                      className="w-100"
                      variant="primary"
                    >
                      {editing ? 'Save' : 'Create'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
